import { Card, Icon } from '@salesforce/design-system-react';
import { useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';

import type { SpecDefinition } from '../qualityFormulaTypes';

import { IconCategory } from '../../../shared/types/Enum';
import { pluralize } from '../pluralize';
import { SPEC_TYPES } from '../qualityFormulaMetadata';
import { useQualityFormulaStore } from '../store/QualityFormulaStoreProvider';
import { FormulaTestingPanel } from './FormulaTestingPanel';
import { PlaygroundScenario } from './PlaygroundScenario';
import { QualityResultsSection } from './QualityResultsSection';

interface PlaygroundPanelProps {
  description?: string;
  enabledSpecs?: SpecDefinition[];
  extraActions?: React.ReactNode;
  /** Forwarded to `PlaygroundScenario` — hide its own icon/title when an outer frame already shows one. */
  showPlaygroundHeading?: boolean;
  showResults?: boolean;
  testButtonLabel?: string;
  workbench?: boolean;
}

const getPlaygroundDescription = (
  description: string | undefined,
  enabledSpecs: SpecDefinition[],
  showResults: boolean,
) => {
  if (showResults)
    return `Try sample values for ${pluralize(enabledSpecs.length, 'spec')}. Your configured terms stay unchanged.`;
  if (description !== undefined) return description;
  return enabledSpecs.length === 0
    ? 'Add a quality spec to test it here — testing never changes a spec’s configured terms.'
    : `Test all ${pluralize(enabledSpecs.length, 'configured spec')} together without changing their terms.`;
};

/**
 * Wraps `FormulaTestingPanel` in its own icon header. The Template Editor passes its
 * selected spec; the workbench tests every enabled spec.
 * The shared `FormulaTestingPanel` keeps input and preview behavior the same in both contexts.
 */
export const PlaygroundPanel: React.FC<PlaygroundPanelProps> = ({
  description,
  enabledSpecs: selectedSpecs,
  extraActions,
  showPlaygroundHeading = true,
  showResults = false,
  testButtonLabel = 'Test These Formulas',
  workbench = false,
}) => {
  const activeSpecs = useQualityFormulaStore(
    useShallow(store => SPEC_TYPES.filter(spec => store.specStates[spec.key].enabled)),
  );
  const enabledSpecs = selectedSpecs ?? activeSpecs;
  const summary = useQualityFormulaStore(store => store.summary);
  const resultsRef = useRef<HTMLDivElement>(null);
  const previousSummaryRef = useRef(summary);

  useEffect(() => {
    if (showResults && summary && summary !== previousSummaryRef.current) {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    previousSummaryRef.current = summary;
  }, [showResults, summary]);

  const testForm = (
    <FormulaTestingPanel
      compact={workbench}
      description=""
      enabledSpecs={enabledSpecs}
      extraActions={extraActions}
      showHeading={false}
      testButtonLabel={testButtonLabel}
      title="Playground"
    />
  );
  if (workbench) {
    return (
      <section aria-label="Playground" className="qfw-playground-surface">
        <PlaygroundScenario enabledSpecs={enabledSpecs} showHeading={showPlaygroundHeading} />
      </section>
    );
  }

  let content = testForm;
  if (showResults) {
    content = (
      <div className="qfw-playground-workspace">
        <div className="qfw-test-scenario">
          <h2 className="slds-text-heading_small">Test Scenario</h2>
          {testForm}
        </div>
        {summary ? (
          <div className="qfw-line-results" ref={resultsRef}>
            <QualityResultsSection inline />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <Card
      className="qfw-playground"
      heading={
        <span className="qfw-playground-heading">
          <Icon
            category={IconCategory.Utility}
            // The Ash/Sulfur/BTU references draw this as a flask/beaker glyph (a literal science
            // experiment icon), not the "preview" eye — `path_experiment` is the matching icon in
            // the installed utility sprite (see public/assets/icons/utility-sprite).
            name="path_experiment"
            size="small"
            style={{ fill: 'var(--slds-g-color-accent-1)' }}
          />
          <span>Playground</span>
        </span>
      }
    >
      <hr className="qfw-divider slds-p-bottom_small" />
      <p className="slds-text-body_regular slds-text-color_weak slds-p-bottom_small">
        {getPlaygroundDescription(description, enabledSpecs, showResults)}
      </p>
      {content}
    </Card>
  );
};
